const DEFAULT_FLUSH_INTERVAL_MS = 30000;
const DEFAULT_MAX_BATCH = 30;
const DEFAULT_FPS_INTERVAL_MS = 15000;
const FPS_SAMPLE_MS = 2000;
const MEDIA_EVENT_THROTTLE_MS = 5000;
const DROPPED_FRAME_LOG_DELTA = 30;
const NOISY_MEDIA_EVENTS = ["waiting", "canplay", "playing", "suspend"];

function now() {
  return window.performance && window.performance.now
    ? window.performance.now()
    : Date.now();
}

function safeRound(value) {
  return typeof value === "number" && isFinite(value)
    ? Math.round(value * 100) / 100
    : value;
}

function videoState(video) {
  if (!video) return null;
  const state = {
    currentSrc: video.currentSrc || video.src || "",
    readyState: video.readyState,
    networkState: video.networkState,
    paused: video.paused,
    ended: video.ended,
    duration: safeRound(video.duration),
    currentTime: safeRound(video.currentTime),
    videoWidth: video.videoWidth || 0,
    videoHeight: video.videoHeight || 0
  };

  if (video.error) {
    state.error = {
      code: video.error.code,
      message: video.error.message || ""
    };
  }

  if (video.getVideoPlaybackQuality) {
    const quality = video.getVideoPlaybackQuality();
    state.playbackQuality = {
      creationTime: safeRound(quality.creationTime),
      totalVideoFrames: quality.totalVideoFrames,
      droppedVideoFrames: quality.droppedVideoFrames,
      corruptedVideoFrames: quality.corruptedVideoFrames
    };
  }

  return state;
}

function slideSummary(slide) {
  if (!slide) return null;
  return {
    title: slide.title || "",
    type: slide.type || "",
    duration: slide.duration || 0,
    theme: slide.theme || "",
    hasImage: Boolean(slide.img),
    hasVideo: Boolean(slide.video),
    hasBackgroundVideo: Boolean(slide.backgroundVideo),
    imageUrl: slide.img ? slide.img.url : "",
    videoUrl: slide.video ? slide.video.url : "",
    backgroundVideoUrl: slide.backgroundVideo ? slide.backgroundVideo.url : ""
  };
}

function mediaSupport() {
  const video = document.createElement("video");
  return {
    mp4: video.canPlayType("video/mp4"),
    h264Baseline: video.canPlayType(
      'video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
    ),
    h264Main: video.canPlayType(
      'video/mp4; codecs="avc1.4D401E, mp4a.40.2"'
    ),
    h264High: video.canPlayType(
      'video/mp4; codecs="avc1.640028, mp4a.40.2"'
    ),
    webmVp8: video.canPlayType('video/webm; codecs="vp8, vorbis"'),
    webmVp9: video.canPlayType('video/webm; codecs="vp9, opus"')
  };
}

function runtimeMeta(config) {
  return {
    href: window.location.href,
    version: config.version || "",
    userAgentFilter: config.userAgentFilter || "",
    userAgent: navigator.userAgent,
    platform: navigator.platform || "",
    language: navigator.language || "",
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    deviceMemory: navigator.deviceMemory || null,
    screen: {
      width: window.screen ? window.screen.width : null,
      height: window.screen ? window.screen.height : null,
      availWidth: window.screen ? window.screen.availWidth : null,
      availHeight: window.screen ? window.screen.availHeight : null
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1
    },
    support: {
      customElements: "customElements" in window,
      fetch: "fetch" in window,
      sendBeacon: "sendBeacon" in navigator,
      performanceObserver: "PerformanceObserver" in window,
      requestAnimationFrame: "requestAnimationFrame" in window,
      videoPlaybackQuality:
        "HTMLVideoElement" in window &&
        "getVideoPlaybackQuality" in HTMLVideoElement.prototype,
      media: mediaSupport()
    }
  };
}

function createSessionId() {
  if (window.crypto && window.crypto.getRandomValues) {
    const values = new Uint32Array(2);
    window.crypto.getRandomValues(values);
    return `${Date.now().toString(36)}-${values[0].toString(36)}${values[1].toString(36)}`;
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function userAgentMatches(filter) {
  if (!filter) return true;
  if (window.navigator && navigator.userAgent) {
    try {
      return new RegExp(filter, "i").test(navigator.userAgent);
    } catch (e) {
      return navigator.userAgent.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
    }
  }
  return false;
}

export class PerfLogger {
  constructor(config) {
    this.config = config || {};
    this.enabled = Boolean(
      this.config.enabled &&
      this.config.endpoint &&
      (this.config.force || userAgentMatches(this.config.userAgentFilter || ""))
    );
    this.endpoint = this.config.endpoint || "";
    this.token = this.config.token || "";
    this.sessionId = createSessionId();
    this.queue = [];
    this.mediaEventState = {};
    this.flushTimer = null;
    this.fpsTimer = null;
    this.longTaskObserver = null;
    this.flushInterval = parseInt(
      this.config.flushInterval || DEFAULT_FLUSH_INTERVAL_MS,
      10
    );
    this.maxBatch = parseInt(this.config.maxBatch || DEFAULT_MAX_BATCH, 10);
    this.fpsInterval = parseInt(
      this.config.fpsInterval || DEFAULT_FPS_INTERVAL_MS,
      10
    );
  }

  start() {
    if (!this.enabled) return;

    this.record("session-start", runtimeMeta(this.config));
    this.#probeDecodeCapabilities();
    this.#observeLongTasks();
    this.#startFpsSampling();

    this.flushTimer = setInterval(() => this.flush(), this.flushInterval);
    document.addEventListener("visibilitychange", this);
    window.addEventListener("pagehide", this);
  }

  stop() {
    if (!this.enabled) return;

    clearInterval(this.flushTimer);
    clearTimeout(this.fpsTimer);
    document.removeEventListener("visibilitychange", this);
    window.removeEventListener("pagehide", this);

    if (this.longTaskObserver) {
      this.longTaskObserver.disconnect();
      this.longTaskObserver = null;
    }

    this.flush(true);
  }

  handleEvent(event) {
    if (event.type === "visibilitychange") {
      if (document.visibilityState === "hidden") this.flush(true);
    } else if (event.type === "pagehide") {
      this.flush(true);
    }
  }

  record(type, data) {
    if (!this.enabled) return;

    this.queue.push({
      type,
      at: new Date().toISOString(),
      t: safeRound(now()),
      data: data || {}
    });

    if (this.queue.length >= this.maxBatch) {
      this.flush();
    }
  }

  recordMediaEvent(event, kind, layer) {
    if (!this.enabled) return;

    const state = videoState(event.target);
    if (!this.#shouldRecordMediaEvent(event, kind, layer, state)) return;

    this.record("media", {
      event: event.type,
      kind,
      active: layer && layer.el ? layer.el.classList.contains("is-active") : false,
      slide: layer ? slideSummary(layer.slide) : null,
      video: state
    });
  }

  recordSlideChange(slide, index, layer) {
    this.record("slide-change", {
      index,
      slide: slideSummary(slide),
      video: layer ? videoState(layer.video) : null,
      backgroundVideo: layer ? videoState(layer.bgVideo) : null
    });
  }

  recordPrepare(slide, layer, duration, reason) {
    this.record("prepare", {
      duration: safeRound(duration),
      reason,
      slide: slideSummary(slide),
      video: layer ? videoState(layer.video) : null,
      backgroundVideo: layer ? videoState(layer.bgVideo) : null
    });
  }

  recordPlayResult(kind, slide, video, error) {
    this.record("play-result", {
      kind,
      ok: !error,
      error: error ? String(error && error.message ? error.message : error) : "",
      slide: slideSummary(slide),
      video: videoState(video)
    });
  }

  flush(useBeacon = false) {
    if (!this.enabled || !this.queue.length) return;

    const events = this.queue.splice(0, this.maxBatch);
    const payload = JSON.stringify({
      token: this.token,
      sessionId: this.sessionId,
      force: Boolean(this.config.force),
      events
    });

    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(this.endpoint, blob)) return;
    }

    if (window.fetch) {
      fetch(this.endpoint, {
        method: "POST",
        credentials: "same-origin",
        keepalive: useBeacon,
        headers: {
          "Content-Type": "application/json"
        },
        body: payload
      }).catch(() => {
        this.queue = events.concat(this.queue).slice(0, this.maxBatch * 2);
      });
    }
  }

  /**
   * Ask the device itself what it can decode, via the Media Capabilities API.
   * `powerEfficient` ~= "hardware-decoded"; `smooth` ~= "expects no stutter".
   * Sweeping codec x resolution turns our inference about software decode into
   * a device-reported fact and maps the actual playback ceiling. Runs once.
   */
  #probeDecodeCapabilities() {
    const mc = navigator.mediaCapabilities;
    if (!mc || !mc.decodingInfo) {
      this.record("decode-capabilities", { supported: false });
      return;
    }

    const codecs = [
      { codec: "h264-high", contentType: 'video/mp4; codecs="avc1.640028"' },
      { codec: "h264-main", contentType: 'video/mp4; codecs="avc1.4d401e"' },
      { codec: "h264-baseline", contentType: 'video/mp4; codecs="avc1.42e01e"' },
      { codec: "hevc", contentType: 'video/mp4; codecs="hev1.1.6.L120.90"' },
      { codec: "vp9", contentType: 'video/webm; codecs="vp09.00.10.08"' }
    ];
    const sizes = [
      { size: "4k", width: 3840, height: 2160 },
      { size: "1080p", width: 1920, height: 1080 },
      { size: "720p", width: 1280, height: 720 }
    ];
    const framerate = 30;
    const bitrate = 4000000;

    const queries = [];
    codecs.forEach(c => {
      sizes.forEach(s => {
        let probe;
        try {
          probe = mc
            .decodingInfo({
              type: "file",
              video: {
                contentType: c.contentType,
                width: s.width,
                height: s.height,
                bitrate,
                framerate
              }
            })
            .then(r => ({
              codec: c.codec,
              size: s.size,
              fps: framerate,
              supported: r.supported,
              smooth: r.smooth,
              powerEfficient: r.powerEfficient
            }))
            .catch(e => ({
              codec: c.codec,
              size: s.size,
              error: String(e && e.message ? e.message : e)
            }));
        } catch (e) {
          probe = Promise.resolve({
            codec: c.codec,
            size: s.size,
            error: String(e && e.message ? e.message : e)
          });
        }
        queries.push(probe);
      });
    });

    Promise.all(queries).then(results => {
      this.record("decode-capabilities", { framerate, bitrate, results });
    });
  }

  #observeLongTasks() {
    if (!window.PerformanceObserver) return;
    const supported = PerformanceObserver.supportedEntryTypes || [];
    if (supported.indexOf("longtask") === -1) return;

    try {
      this.longTaskObserver = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          this.record("longtask", {
            name: entry.name,
            startTime: safeRound(entry.startTime),
            duration: safeRound(entry.duration)
          });
        });
      });
      this.longTaskObserver.observe({ entryTypes: ["longtask"] });
    } catch (e) {
      this.record("unsupported", { feature: "longtask", error: String(e) });
    }
  }

  #shouldRecordMediaEvent(event, kind, layer, state) {
    if (NOISY_MEDIA_EVENTS.indexOf(event.type) === -1) return true;

    const src = state ? state.currentSrc : "";
    const slide = layer && layer.slide ? layer.slide.title || "" : "";
    const active = layer && layer.el && layer.el.classList.contains("is-active")
      ? "active"
      : "buffer";
    const key = `${kind}|${event.type}|${active}|${slide}|${src}`;
    const currentTime = now();
    const dropped = state && state.playbackQuality
      ? state.playbackQuality.droppedVideoFrames || 0
      : 0;
    const previous = this.mediaEventState[key];

    if (
      previous &&
      currentTime - previous.time < MEDIA_EVENT_THROTTLE_MS &&
      dropped - previous.dropped < DROPPED_FRAME_LOG_DELTA
    ) {
      return false;
    }

    this.mediaEventState[key] = {
      time: currentTime,
      dropped
    };
    return true;
  }

  #startFpsSampling() {
    if (!window.requestAnimationFrame) return;
    const sample = () => {
      const start = now();
      let frames = 0;
      const tick = () => {
        frames++;
        const elapsed = now() - start;
        if (elapsed >= FPS_SAMPLE_MS) {
          this.record("fps", {
            sampleMs: safeRound(elapsed),
            frames,
            fps: safeRound((frames * 1000) / elapsed)
          });
          this.fpsTimer = setTimeout(sample, this.fpsInterval);
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    this.fpsTimer = setTimeout(sample, 1000);
  }
}
